import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const TermsPage: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <div className="pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
          
          <div className="prose prose-lg prose-invert">
            <p className="text-gray-300 mb-6">Last Updated: October 15, 2025</p>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">1. Introduction</h2>
              <p className="text-gray-300 mb-4">
                Welcome to TattooNailGenius. These Terms of Service govern your use of our website and services. By accessing or using our platform, you agree to be bound by these terms.
              </p>
              <p className="text-gray-300">
                Please read these terms carefully before using our service. If you do not agree to these terms, please do not use our platform.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">2. Definitions</h2>
              <p className="text-gray-300 mb-4">
                <strong>"Service"</strong> refers to the TattooNailGenius website and AI design generation platform.
              </p>
              <p className="text-gray-300 mb-4">
                <strong>"User"</strong> refers to any individual who accesses or uses our Service.
              </p>
              <p className="text-gray-300">
                <strong>"Content"</strong> refers to all designs, images, text, and other materials generated or displayed through our Service.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">3. Use of Service</h2>
              <p className="text-gray-300 mb-4">
                Our Service provides AI-generated designs for tattoos and nail art. Users may:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300">
                <li>Generate designs using our AI technology</li>
                <li>Download generated designs for personal use</li>
                <li>Share designs through our platform</li>
              </ul>
              <p className="text-gray-300">
                You must not use our Service for any illegal or unauthorized purpose. You agree to comply with all laws, rules, and regulations applicable to your use of the Service.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">4. User Accounts</h2>
              <p className="text-gray-300 mb-4">
                Some features of our Service may require registration. When you register, you agree to provide accurate information and keep it up to date.
              </p>
              <p className="text-gray-300 mb-4">
                You are responsible for maintaining the confidentiality of your account and password and for restricting access to your computer. You agree to accept responsibility for all activities that occur under your account.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">5. Intellectual Property</h2>
              <p className="text-gray-300 mb-4">
                The designs generated through our Service are provided for personal use only. By downloading a design, you receive a non-exclusive, non-transferable license to use the design for personal purposes.
              </p>
              <p className="text-gray-300 mb-4">
                For commercial use of any design, explicit permission must be obtained from TattooNailGenius.
              </p>
              <p className="text-gray-300">
                The Service itself, including its underlying technology, is owned by TattooNailGenius and is protected by copyright, trademark, and other intellectual property laws.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">6. User Content</h2>
              <p className="text-gray-300 mb-4">
                Our Service may allow users to provide prompts, reference images, or other content to generate designs. You retain ownership of any such content you provide.
              </p>
              <p className="text-gray-300">
                By providing content to our Service, you grant TattooNailGenius a worldwide, non-exclusive, royalty-free license to use, reproduce, process, and display such content solely for the purpose of providing our Service.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">7. Disclaimers</h2>
              <p className="text-gray-300 mb-4">
                Our Service is provided "as is" without warranties of any kind, either express or implied. TattooNailGenius does not guarantee that the Service will be uninterrupted, secure, or error-free.
              </p>
              <p className="text-gray-300 mb-4">
                Designs generated through our Service are created by artificial intelligence and may not be suitable for direct use as tattoos or nail art without professional consultation.
              </p>
              <p className="text-gray-300">
                We strongly recommend consulting with a professional tattoo artist or nail technician before applying any design generated through our Service.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-300 mb-4">
                To the maximum extent permitted by law, TattooNailGenius shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use the Service.
              </p>
              <p className="text-gray-300">
                This includes but is not limited to damages for loss of profits, goodwill, data, or other intangible losses.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">9. Modifications to Terms</h2>
              <p className="text-gray-300 mb-4">
                TattooNailGenius reserves the right to modify these Terms at any time. We will provide notice of significant changes by posting an announcement on our platform or sending an email to registered users.
              </p>
              <p className="text-gray-300">
                Your continued use of the Service after such modifications constitutes your acceptance of the revised Terms.
              </p>
            </section>
            
            <section className="mb-8">
              <h2 className="text-xl font-bold mb-4">10. Governing Law</h2>
              <p className="text-gray-300">
                These Terms shall be governed by the laws of the jurisdiction in which TattooNailGenius is established, without regard to its conflict of law provisions.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-bold mb-4">11. Contact Information</h2>
              <p className="text-gray-300">
                If you have any questions about these Terms, please contact us at info@mramoreno.tech.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;